import type { PropType } from "vue";
import type { Stars } from "./model-ratings";
import type { Model } from "@/schemas/model.schema";
import { computed, defineComponent } from "vue";
import Badge from "@/components/ui/badge/Badge";
import Icon from "@/components/ui/icon/Icon";
import Tooltip from "@/components/ui/tooltip/Tooltip";
import { getModelDisplayName } from "@/schemas/model.schema";
import { $t } from "@/services/i18n";
import { getModelIcon, getModelObject } from "@/stores/model.store";
import { cn } from "@/utils/cn";
import { deprecationTooltipText } from "@/utils/model-deprecation";
import { p } from "@/utils/props-builder";
import { providerLabel } from "@/utils/provider-labels";
import { billingStateOf } from "./model-billing";
import { isDeprecated } from "./model-ratings";
import StarRating from "./StarRating";

export default defineComponent({
  name: "ModelCard",
  props: {
    model: {
      type: [String, Object] as PropType<string | Model>,
      required: true,
    },
    /** Shows the 3 mini rating stars alongside the name. Used by the picker trigger. */
    showRatings: p.boolean(false),
    /**
     * Precomputed relative stars for this model. Stars are relative to the shown
     * set, so the caller (which owns the set) computes them and passes them in —
     * ModelCard never computes them in isolation.
     */
    stars: {
      type: Object as PropType<Stars | null>,
      default: null,
    },
  },
  setup(props) {
    const model = computed(() => getModelObject(props.model));
    const icon = computed(() => getModelIcon(model.value));
    const deprecated = computed(() => !!model.value && isDeprecated(model.value));

    const deprecationTooltip = computed(() => deprecationTooltipText(model.value));

    // Who bills the selected model. Repeated here (not only in the dropdown
    // row) so the answer survives the dropdown closing - the trigger is what
    // the user actually stares at while configuring the agent.
    const billing = computed(() => (model.value ? billingStateOf(model.value) : "platform"));
    const provider = computed(() =>
      providerLabel(model.value?.credential_key ?? model.value?.provider)
    );
    const displayName = computed(() => getModelDisplayName(model.value) || props.model);

    // The model reasons but the provider hands back no text: worth saying at the
    // point of choice, so nobody turns reasoning on expecting to read it. An absent
    // capability is unprobed, not silent, so it stays quiet.
    const reasoningTextHidden = computed(() => {
      const visibility = model.value?.capabilities?.reasoning_visibility;
      return visibility === "tokens_only" || visibility === "none";
    });

    // Only render the cluster when we actually have at least one star to show.
    const hasStars = computed(
      () =>
        props.showRatings &&
        !!props.stars &&
        (props.stars.speed != null ||
          props.stars.intelligence != null ||
          props.stars.cost != null)
    );

    return () => (
      <div class="flex min-w-0 items-center gap-2 whitespace-nowrap text-left">
        <Icon class="h-4 w-4 flex-none opacity-70" icon={icon.value} />
        <span class={cn("truncate", deprecated.value && "line-through")}>
          {displayName.value}
        </span>
        {!!model.value?.experimental && (
          <Tooltip text={$t("agents:experimental_model_tooltip")} class="max-w-60 text-wrap">
            <Badge color="amber" size="xs" class="flex-none scale-90">
              {$t("agents:experimental_label")}
            </Badge>
          </Tooltip>
        )}
        {deprecated.value && (
          <Tooltip text={deprecationTooltip.value} class="max-w-60 text-wrap">
            <Badge color="red" size="xs" class="flex-none scale-90">
              {$t("deprecated")}
            </Badge>
          </Tooltip>
        )}
        {billing.value === "byok_active" && (
          <Tooltip
            text={$t("agents:byok_active_tooltip", { provider: provider.value })}
            class="max-w-72 text-wrap"
          >
            <Badge color="green" size="xs" class="flex-none scale-90 gap-1">
              <Icon icon="lucide:key-round" class="h-3 w-3" aria-hidden="true" />
              {provider.value}
            </Badge>
          </Tooltip>
        )}
        {billing.value === "byok_required" && (
          <Tooltip
            text={$t("agents:byok_required_tooltip", { provider: provider.value })}
            class="max-w-72 text-wrap"
          >
            <Badge color="amber" size="xs" class="flex-none scale-90 gap-1">
              <Icon icon="lucide:key-round" class="h-3 w-3" aria-hidden="true" />
              {$t("agents:byok_required_label")}
            </Badge>
          </Tooltip>
        )}
        {reasoningTextHidden.value && (
          <Tooltip text={$t("agents:reasoning_visibility_tooltip")} class="max-w-72 text-wrap">
            <Icon
              icon="lucide:brain"
              class="h-3.5 w-3.5 flex-none cursor-help text-muted-foreground/70"
              aria-label={$t("agents:reasoning_visibility_tooltip")}
            />
          </Tooltip>
        )}
        {hasStars.value && props.stars && (
          <div class="ml-auto flex flex-none items-center gap-2 pl-2">
            <StarRating dimension="speed" value={props.stars.speed} />
            <StarRating dimension="intelligence" value={props.stars.intelligence} />
            <StarRating dimension="cost" value={props.stars.cost} />
          </div>
        )}
      </div>
    );
  },
});
